import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
export const meta = {
  title: "House Fire Statistics 2025",
  description: "Discover key house fire stats, common causes, costs, and how homeowners insurance can help protect you with Guardian Service.",
  category: "General",
  tags: [],
  featuredImage: {
    src: "/images/blog/AdobeStock_647004426-scaled.webp",
    alt: "House Fire Statistics 2025"
  },
  author: {
    name: "Dash Lewis, Kara Credle",
    role: "Author",
    image: ""
  },
  publishedAt: "June 13, 2025",
  updatedAt: "July 3, 2025",
  showTOC: true
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    em: "em",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    hr: "hr",
    img: "img",
    li: "li",
    p: "p",
    strong: "strong",
    table: "table",
    tbody: "tbody",
    td: "td",
    th: "th",
    thead: "thead",
    tr: "tr",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      children: meta.title
    }), "\n", meta.description, "\n", _jsx(_components.hr, {}), "\n", _jsxs(_components.p, {
      children: ["Once a house fire starts, it can grow quickly and without restraint, becoming a major fire in under three minutes. House fires are a common occurrence: around 944 house fires occur every day. “The most common causes of residential fires are cooking and heating equipment, electric short circuit, smoking (inside homes), and flammable liquids and open flames,” says ", _jsx(_components.a, {
        href: "https://engineering.msu.edu/faculty/Venkatesh-Kodur",
        children: "Venkatesh Kodur"
      }), ", University Distinguished Professor and Director of the Centre on Structural Fire Engineering and Diagnostics at Michigan State University. Home fires are more common than you may think, and can escalate quickly, impacting the structural integrity and safety of your home."]
    }), "\n", _jsxs(_components.p, {
      children: ["In this article, we analyzed data from the United States Fire Administration and FEMA to explore house fire trends and statistics, including how much house fires can cost homeowners. Most ", _jsx(_components.a, {
        href: "/insurance/home",
        children: "homeowners insurance policies"
      }), " cover house fires, but it’s important to know the limits of your policy. We interviewed experts in structural and safety engineering to get their tips for mitigating fire risk at home and understand the insurance impacts of house fires."]
    }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
      children: "Key Takeaways"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "There were 344,600 house fires in 2023, accounting for about 25% of all fires"
      }), "\n", _jsx(_components.li, {
        children: "Cooking is the leading cause of house fires, accounting for nearly 49% of all residential building fires in 2023."
      }), "\n", _jsx(_components.li, {
        children: "House fires can escalate from a small flame to a major fire in just two and a half minutes, with flashover occurring within three minutes."
      }), "\n", _jsx(_components.li, {
        children: "The total cost of residential fire damage was $11.3 billion in 2023, with an average restoration cost of $27,175."
      }), "\n", _jsxs(_components.li, {
        children: ["Most homeowners insurance covers ", _jsx(_components.a, {
          href: "/home-insurance/insurance-perils",
          children: "fire damage"
        }), " through dwelling, personal property, and other structures coverage, but insurers are increasingly struggling with rising costs and may limit coverage in high-risk areas."]
      }), "\n"]
    }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
      children: "House Fire Trends"
    }), "\n", _jsxs(_components.p, {
      children: ["According to data collected by the ", _jsx(_components.a, {
        href: "https://www.usfa.fema.gov/statistics/",
        children: "United States Fire Administration"
      }), ", there were 344,600 residential structure fires in 2023, which accounted for just about 25% of all fires that year (1,389,000 ) . There was a decrease in fires year-over-year, as there were over 1.5 million fires in 2022, but the number of fires has risen by 9.1% since 2014."]
    }), "\n", _jsx(_components.p, {
      children: "The amount of residential fires has been decreasing slightly over that same period. In 2014, FEMA recorded 379,500 residential structure fires. Though it has fluctuated over the years, the amount has steadily trended down."
    }), "\n", _jsx(_components.p, {
      children: "The total cost of damage caused by fires in 2023 was $23 billion, with residential accounting for $11.3 billion. Total property damage loss only increased by around 5% from 2021 to 2022, but jumped by nearly 21% from 2022 to 2023."
    }), "\n", _jsx(_components.h4, {
      children: "Areas Most at Risk"
    }), "\n", _jsxs(_components.p, {
      children: ["Cooking accounts for 48.7% of all residential fires in 2023 according to FEMA. Other top causes include: ‘unintentional’ and ‘careless’ (9.2%), heating (8.1%), and electrical malfunction (6.9%), ", _jsx(_components.a, {
        href: "https://www.eku.edu/personnel/dr-gregory-gorbett/",
        children: "Dr. Gregory E. Gorbett"
      }), ", Professor at Eastern Kentucky University teaching in the Fire Protection and Safety Engineering Technology Program identifies that “the kitchen is the most common area of origin for fires, as cooking is the number one cause. The second most common area of origin is the bedroom.” He also notes that homeowners should be particularly careful in “living arrangements with multiple tenants, such as apartments, townhouses, or joint living arrangements, where you must rely on others to be attentive to cooking and smoking.”"]
    }), "\n", _jsxs(_components.p, {
      children: ["Areas with frequent wildfires are also at risk and can lead homeowners in these areas to have higher insurance premiums because of it. ", _jsx(_components.a, {
        href: "https://fpe.umd.edu/clark/faculty/633/Peter-B-Sunderland",
        children: "Peter Sunderland"
      }), ", Professor of Fire Protection Engineering at the University of Maryland adds that “wildland fires are like floods — they seem to return where they have passed before. At least this is the perspective of insurance companies, who have the best data and who charge higher premiums where wildland fires have burned before.”"]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.img, {
        src: "/images/blog/AdobeStock_1096903008-1024x574.webp",
        alt: "Residential house fire in a high-risk area"
      })
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.em, {
        children: "Credit: Adobe – Royalty Free"
      })
    }), "\n", _jsx(_components.h4, {
      children: "Climate Change and Modern Fire Risks"
    }), "\n", _jsx(_components.p, {
      children: "The relationship between climate change and fire risk is becoming increasingly complex. Kodur notes that climate change affects homes both directly and indirectly: “Directly the rising atmospheric temperatures, drier conditions, and changes in weather patterns, all resulting from global warming effects, provide ideal conditions for faster ignition and rapid spread (and severity) of these fires. Indirectly, many of the newer construction materials and structural shapes being introduced for houses to address sustainability and climate change effects, are indirectly making modern constructions (especially houses in wild fire areas) highly vulnerable to adverse effects of fire.”"
    }), "\n", _jsx("iframe", {
      title: "house fire quote",
      src: "https://e.infogram.com/2a2941a2-afa4-435b-8148-82f2049d6ec0?src=embed&embed_type=responsive_iframe",
      allowFullScreen: "allowfullscreen"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "https://eecs.utk.edu/people/david-icove/",
        children: "David Icove"
      }), ", Professor of Practice at the University of Tennessee, describes the cascading effects: “As global temperatures rise, both vegetation and soil dry out, enhancing their susceptibility to ignition and allowing fires to spread quickly. Altered precipitation patterns, marked by more intense and frequent droughts, cause further drying of vegetation and longer dry seasons, effectively extending the fire season.”"]
    }), "\n", _jsx(_components.h3, {
      children: "Cost of House Fires"
    }), "\n", _jsxs(_components.p, {
      children: ["In 2023, the estimated property damage losses were $23 billion. Rebuilding after a fire is not cheap. According to ", _jsx(_components.a, {
        href: "https://www.angi.com/articles/how-much-does-fire-damage-restoration-cost.htm",
        children: "Angi"
      }), ", fire damage restoration costs can range from ", _jsx(_components.strong, {
        children: "$3,107"
      }), " to ", _jsx(_components.strong, {
        children: "$51,243"
      }), ", with an average restoration cost of ", _jsx(_components.strong, {
        children: "$27,175"
      }), ". If your house fire isn’t severe, you might only have to pay a modest sum to clean up smoke damage. But significant structural damage requires a substantial amount of materials and labor, and thus can add up quickly."]
    }), "\n", _jsx(_components.h4, {
      children: "Does Homeowners Insurance Cover Fires?"
    }), "\n", _jsxs(_components.p, {
      children: ["Most ", _jsx(_components.a, {
        href: "/home-insurance/homeowners-insurance-awareness",
        children: "homeowners insurance"
      }), " policies include fire damage under the following coverages:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Dwelling:"
        }), " This coverage protects the structure of a home and can pay for the cost of repairs or rebuilding. If your home is uninhabitable, dwelling coverage can help cover the costs of hotel rooms and restaurant bills while construction takes place."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Personal property:"
        }), " This coverage helps cover the cost of repairing or replacing personal property that’s damaged in a house fire, such as clothing, furniture, and electronics."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Other structures:"
        }), " Sometimes referred to as “detached structures coverage,” this helps to pay the cost of repairing or rebuilding structures not attached to your home that are damaged by fire, such as sheds, garages, and fences."]
      }), "\n"]
    }), "\n", _jsx(_components.h4, {
      children: "Insurance Industry Challenges"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "https://www.bryant.edu/academics/faculty/gorvett-rick",
        children: "Rick Gorvett"
      }), ", Professor and Chair of the Mathematics Department at Bryant University, explains that emerging risks are reshaping the insurance landscape: “Two kinds of risks are currently re-shaping the homeowners insurance landscape: wildfires, and coastal storms (in particular, Atlantic hurricanes). Recent developments with respect to both of these risks – greater frequency of events, and increased cost and severity of the resulting damage – have led to higher insurance premiums, and in some cases even unavailability of coverage.”"]
    }), "\n", _jsx(_components.p, {
      children: "He points to California as a prime example: “Evolving and increasing fire risks in California have led to several insurers being unprofitable in this line of business, and to consider not writing new homeowners insurance policies in the state.”"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "https://wne.edu/directory/yixiao-jiang.cfm",
        children: "Yixiao (Ethan) Jiang"
      }), ", Assistant Professor of Finance at Western New England University, notes how modern technology is changing the insurance landscape: “Climate change and urban expansion have significantly increased fire risks, making them more systemic and challenging to insure against. The traditional insurance model relies on the Law of Large Numbers, pooling numerous independent risks to predict average losses accurately. However, climate-induced events, such as widespread wildfires, impact large areas simultaneously, disrupting this model.”"]
    }), "\n", _jsx(_components.h4, {
      children: "Insurance Coverage Gaps and Solutions"
    }), "\n", _jsx(_components.p, {
      children: "Gorvett identifies several potential gaps in fire damage coverage:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Any applicable deductibles or inadequate coverage limits on certain categories of property"
      }), "\n", _jsx(_components.li, {
        children: "Delays in insurance proceeds due to administrative requirements"
      }), "\n", _jsx(_components.li, {
        children: "The challenge for insurers to keep up with rapidly emerging fire risk trends"
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "Jiang recommends exploring modern insurance solutions: “Homeowners should explore InsurTech (insurance technology) companies that leverage advanced tools like AI, drones, and satellite imagery to assess fire risks at the individual property level. Traditional insurers often rely on broader metrics, such as ZIP code averages, which may not accurately reflect a specific property’s risk.”"
    }), "\n", _jsx(_components.h4, {
      children: "Expert Advice for Getting Fire Insurance"
    }), "\n", _jsx(_components.p, {
      children: "Gorvett recommends several strategies:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Occasionally shop around for homeowners insurance and get multiple quotes"
      }), "\n", _jsx(_components.li, {
        children: "Try to understand the insurance market in your particular locale"
      }), "\n", _jsx(_components.li, {
        children: "Understand the risks you face as a homeowner, particularly based on location and regional weather patterns"
      }), "\n", _jsx(_components.li, {
        children: "Consider bundling multiple insurance coverages with one insurer for potential discounts"
      }), "\n", _jsx(_components.li, {
        children: "Be aware of the differences in coverage between replacement cost and market value"
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "https://www.uta.edu/academics/faculty/profile?user=sarkar",
        children: "Salil Sarkar"
      }), ", Professor in the College of Business at the University of Texas at Arlington, also recommends asking for discounts like bundling discounts and no-claim discounts. Homeowners may also want to ensure they install a monitored fire alarm because “most companies will give a discount for a monitored fire alarm” says Sarkar."]
    }), "\n", _jsx(_components.h4, {
      children: "The Cost of Rebuilding After a House Fire"
    }), "\n", _jsx(_components.p, {
      children: "The cost of rebuilding a house after a fire depends on many factors, including the severity of the fire itself. The table below shows the average cost of the various kinds of damage that may occur from a home fire according to Angi."
    }), "\n", _jsxs(_components.table, {
      children: [_jsx(_components.thead, {
        children: _jsxs(_components.tr, {
          children: [_jsx(_components.th, {
            children: "Type of Damage"
          }), _jsx(_components.th, {
            children: "Cost"
          })]
        })
      }), _jsxs(_components.tbody, {
        children: [_jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Soot Removal"
          }), _jsx(_components.td, {
            children: "$2,000 to $6,000"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Water"
          }), _jsx(_components.td, {
            children: "$1,000 to $6,000"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Smoke"
          }), _jsx(_components.td, {
            children: "$200 to $1,200"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Damaged Item Removal"
          }), _jsx(_components.td, {
            children: "$50 to $100 per pound"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Furniture Deodorizing"
          }), _jsx(_components.td, {
            children: "$200 to $1,000"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Ozone Treatment"
          }), _jsx(_components.td, {
            children: "$400 to $800"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Duct Cleaning"
          }), _jsx(_components.td, {
            children: "$250 to $500"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Thermal Fogging"
          }), _jsx(_components.td, {
            children: "$200 to $600"
          })]
        })]
      })]
    }), "\n", _jsx(_components.p, {
      children: "There are a few other factors that affect the cost of rebuilding after a fire, including:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "The size of your home."
        }), " A larger home will be more expensive to rebuild."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "The severity of the fire."
        }), " Electrical fires tend to cause the most damage, followed by cooking fires and gas fires."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Your insurance coverage."
        }), " While homeowners insurance generally covers fire damage, the amount of coverage depends on your insurer and the terms of your policy. Keep in mind that you’ll need to meet a deductible before the policy kicks in."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "How quickly emergency services arrive."
        }), " The faster the response time, the less damage your home will incur, thus reducing the cost of rebuilding."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Any pre-existing conditions in your home."
        }), " If your house has outdated wiring or structural issues that could be exacerbated by a fire, it will likely cost more to rebuild."]
      }), "\n"]
    }), "\n", _jsx(_components.h3, {
      children: "How Hot Does a House Fire Get?"
    }), "\n", _jsxs(_components.p, {
      children: ["The average ", _jsx(_components.a, {
        href: "https://readywisconsin.wi.gov/house-fires/",
        children: "house fire temperature"
      }), " can reach more than 1000°F. Those numbers can skyrocket if the fire finds a significant fuel source, reaching upwards of 2,500°F."]
    }), "\n", _jsx(_components.p, {
      children: "There are three general temperature ranges that fires can fall into: Low Range (300-600°F), Normal Range (600-1,100°F), and High Range (1,100-2,000+°F)."
    }), "\n", _jsxs(_components.table, {
      children: [_jsx(_components.thead, {
        children: _jsxs(_components.tr, {
          children: [_jsx(_components.th, {
            children: "Temperature Range"
          }), _jsx(_components.th, {
            children: "Description"
          })]
        })
      }), _jsxs(_components.tbody, {
        children: [_jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "300-600°F (Low Range)"
          }), _jsx(_components.td, {
            children: "Initial stages of a fire, can be easily extinguished but requires quick action"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "600-1,100°F (Normal Range)"
          }), _jsx(_components.td, {
            children: "Can cause significant damage, can be contained but requires professional assistance"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "1,100-2,000+°F (High Range)"
          }), _jsx(_components.td, {
            children: "Extremely dangerous, can lead to total loss of property, significant flashover risk"
          })]
        })]
      })]
    }), "\n", _jsx(_components.p, {
      children: "The intensity of a house fire depends on several factors, including the amount of oxygen available to feed the flames, the fuel sources present, and the initial cause of the fire."
    }), "\n", _jsx(_components.h4, {
      children: "How Long Does It Take for a Flame To Grow Into a Major Fire?"
    }), "\n", _jsx(_components.p, {
      children: "Home fires can spread exceedingly fast, becoming all-consuming within a matter of minutes. The key to preventing a total loss house fire is containing it before the flashover, which is the moment when every combustible material in an enclosed area ignites. The flashover can occur within three minutes of a fire starting."
    }), "\n", _jsxs(_components.p, {
      children: ["According to ", _jsx(_components.a, {
        href: "https://www.biddefordmaine.org/2406/Growth-Speed-of-Fire",
        children: "fire"
      }), " ", _jsx(_components.a, {
        href: "https://www.wrightstownpa.org/media/1641/home-fire-timeline.pdf",
        children: "departments"
      }), " around the country, a flame can escalate into a major house fire in two and a half minutes."]
    }), "\n", _jsxs(_components.h3, {
      children: ["Tips for Mitigating House Fire Risk", _jsx(_components.img, {
        src: "/images/blog/AdobeStock_1151772251-1024x576.webp",
        alt: "smoke alarm detecting fire"
      })]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.em, {
        children: "Credit: Adobe – Royalty Free"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["“To reduce the risk of fire damage, homeowners should prioritize both fire detection and prevention measure” says ", _jsx(_components.a, {
        href: "https://case.edu/issacs/facultyfaculty-associates/ya-ting-liao",
        children: "Dr. Ya-Ting T. Liao"
      }), ", associate professor and the faculty director of the UL Fire and Combustion Laboratories at Case Western Reserve University. “First and foremost, it’s essential to ensure that smoke alarms are properly installed and functioning. Test them regularly and replace their batteries as needed. Keeping fire extinguishers accessible—especially in the kitchen and other high-risk areas—can significantly reduce potential fire damage.”"]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Fire Alarms and Smoke Detectors"
      })
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Conduct monthly tests and replace batteries annually."
      }), "\n", _jsx(_components.li, {
        children: "Consider switching to ten-year battery alarms."
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Cooking"
      })
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Turn off the stove if you leave the kitchen, even for just a moment."
      }), "\n", _jsx(_components.li, {
        children: "Stay in the room when frying, grilling, or broiling food."
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Electrical Safety"
      })
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Replace all old, worn, or damaged appliance cords."
      }), "\n", _jsx(_components.li, {
        children: "Immediately shut off and professionally replace light switches that are hot to the touch."
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Fireplaces and Wood Stoves"
      })
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Check the chimney and stove pipes for obstructions and clean them out."
      }), "\n", _jsx(_components.li, {
        children: "Make sure the fire is completely extinguished before going to bed."
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Space Heaters"
      })
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Keep combustible objects at least three feet away from portable heaters."
      }), "\n", _jsx(_components.li, {
        children: "Only purchase and use portable heaters with thermostat control mechanisms that shut the heater off if it falls over."
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "You should also create an evacuation plan for you and your family in the event of a fire. “To lower the risk of fire damage, create a fire escape plan that outlines two exits from each room and a designated outdoor meeting area,” says Icove."
    }), "\n", _jsx(_components.h3, {
      children: "What To Do After a Fire"
    }), "\n", _jsx(_components.p, {
      children: "In the unfortunate event that you do experience a house fire, here are the steps you should take in the aftermath."
    }), "\n", _jsx(_components.h4, {
      children: "Contact Your Insurance Agent"
    }), "\n", _jsx(_components.p, {
      children: "You should immediately contact your insurance agent and explain the situation. They can help you determine exactly what your policy covers and connect you with companies that specialize in cleaning and rebuilding. When you contact your insurer, be sure to get all the information they give you — especially cost estimates — in writing."
    }), "\n", _jsx(_components.h4, {
      children: "Contact Local Disaster Service"
    }), "\n", _jsx(_components.p, {
      children: "Local disaster relief services, such as the American Red Cross or the Salvation Army, can help you and your family find clothing, food, and shelter."
    }), "\n", _jsx(_components.h4, {
      children: "Comfort Your Pets"
    }), "\n", _jsx(_components.p, {
      children: "If you have pets, they will likely be scared, so handle them with care. See if you can leave them with family or your veterinarian while you clean and repair your damaged home."
    }), "\n", _jsx(_components.h4, {
      children: "Avoid Entering Your Damaged Home"
    }), "\n", _jsx(_components.p, {
      children: "Do not enter your damaged home until the fire department deems it safe to do so. The department will determine whether it’s safe to use utilities and have them shut off accordingly. You should also contact your local police department and explain that you will not be staying in your home. In some cases, you may want to board windows and doors."
    }), "\n", _jsx(_components.h4, {
      children: "Get Your Finances in Order"
    }), "\n", _jsx(_components.p, {
      children: "You should first get in touch with your mortgage lender to bring them up to speed. Second, call your credit card company to report any cards lost or damaged in the fire."
    }), "\n", _jsx(_components.p, {
      children: "Be sure to save all receipts for money you spend while rebuilding your home. You will need them for any reimbursements from your insurance company and to claim losses on your tax return."
    }), "\n", _jsx(_components.h4, {
      children: "Determine the Value of Your Home and Belongings"
    }), "\n", _jsx(_components.p, {
      children: "You’ll need to coordinate with your insurance company to catalog all belongings lost in the fire and determine the value of everything damaged or destroyed."
    }), "\n", _jsx(_components.h4, {
      children: "Replace Valuable Documents"
    }), "\n", _jsx(_components.p, {
      children: "If you’ve lost important documents like your driver’s license, marriage license, social security and Medicare cards, and citizenship papers, begin the process of replacing them as soon as possible."
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
